// src/portals/parent/tabs/desktop/ActionsTab.tsx
import React, { useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiFileText,
} from "react-icons/fi";
import { LuHourglass } from "react-icons/lu";
import TripConsentForm from "../../trips/TripConsentForm";
import ParentMeetingInvitationInlineCard from "../../meeting/ParentMeetingInvitationInlineCard";

const AlertTriangleIcon = FiAlertTriangle as any;
const CheckCircleIcon = FiCheckCircle as any;
const ChevronRightIcon = FiChevronRight as any;
const ClockIcon = FiClock as any;
const FileTextIcon = FiFileText as any;
const HourglassIcon = LuHourglass as any;
const TripConsentFormAny = TripConsentForm as any;

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const SlideIn = ({ children, direction = "right", className = "" }: any) => {
  const [entered, setEntered] = useState(false);

  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const offset = direction === "left" ? "-18px" : "18px";

  return (
    <div
      className={className}
      style={{
        opacity: entered ? 1 : 0,
        transform: entered ? "translateX(0)" : `translateX(${offset})`,
        transition:
          "opacity 220ms ease-out, transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
};

const AppCard = ({ children, className = "" }: any) => (
  <div
    className={cx(
      "rounded-3xl border border-slate-200/80 bg-white/95 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/70",
      className,
    )}
  >
    {children}
  </div>
);

const IconBubble = ({ tone = "emerald", children, compact = false }: any) => {
  const toneClass =
    tone === "amber"
      ? "bg-amber-50 text-amber-700 ring-amber-100 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-800/50"
      : tone === "rose"
        ? "bg-rose-50 text-rose-700 ring-rose-100 dark:bg-rose-900/40 dark:text-rose-100 dark:ring-rose-800/50"
        : tone === "sky"
          ? "bg-sky-50 text-sky-700 ring-sky-100 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-800/50"
          : "bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-100 dark:ring-emerald-800/50";

  return (
    <span
      className={cx(
        "flex flex-shrink-0 items-center justify-center rounded-full ring-1",
        compact ? "h-9 w-9" : "h-11 w-11",
        toneClass,
      )}
    >
      {children}
    </span>
  );
};

const PrimaryButton = ({ children, className = "", ...buttonProps }: any) => (
  <button
    type="button"
    className={cx(
      "inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
      className,
    )}
    {...buttonProps}
  >
    {children}
  </button>
);

const SecondaryButton = ({
  children,
  tone = "slate",
  className = "",
  ...buttonProps
}: any) => {
  const toneClass =
    tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 focus-visible:ring-amber-300 dark:border-amber-700/60 dark:bg-amber-900/40 dark:text-amber-100"
      : tone === "rose"
        ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 focus-visible:ring-rose-200 dark:border-rose-800/60 dark:bg-rose-900/40 dark:text-rose-100"
        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus-visible:ring-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

  return (
    <button
      type="button"
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-full border px-4 py-2 text-xs font-bold shadow-sm transition focus-visible:outline-none focus-visible:ring-2",
        toneClass,
        className,
      )}
      {...buttonProps}
    >
      {children}
    </button>
  );
};

const RequestRow = ({
  icon,
  tone = "emerald",
  title,
  subtitle,
  meta,
  badge,
  onClick,
  children,
}: any) => {
  const body = (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <IconBubble tone={tone} compact>
        {icon}
      </IconBubble>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-[15px] font-bold text-slate-950 dark:text-white">
            {title}
          </p>
          {badge && (
            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 ring-1 ring-amber-100 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-800/60">
              {badge}
            </span>
          )}
        </div>
        {subtitle && (
          <p className="mt-0.5 truncate text-sm text-slate-500 dark:text-slate-300">
            {subtitle}
          </p>
        )}
        {meta && (
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            {meta}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <li className="border-b border-slate-100 last:border-b-0 dark:border-slate-800/80">
      <div className="flex items-center gap-3 px-4 py-3.5 transition hover:bg-slate-50/70 dark:hover:bg-slate-900/60">
        {onClick ? (
          <button
            type="button"
            onClick={onClick}
            className="flex min-w-0 flex-1 items-center text-left"
          >
            {body}
          </button>
        ) : (
          body
        )}
        {children && (
          <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
            {children}
          </div>
        )}
      </div>
    </li>
  );
};

const ActionsTab = (props: any) => {
  const {
    t,
    missingChildren = [],
    onFixChild = (_child: any) => {},
    alumniChildren = [],
    onAlumniAction = (_childId: any) => {},
    outingActions = [],
    onOpenOuting = (_req: any) => {},
    privacyActions = [],
    onOpenPrivacyAction = (_child: any) => {},
    healthActions = [],
    onOpenHealthAction = (_child: any) => {},
    requests = [],
    parentMeetingInvitations = [],
    onSelectRequest = (_req: any) => {},
    tripFormDraft = null,
    onTripFormChange = (_field: any, _value: any) => {},
    onTripFormSubmit = (_request: any, _draft: any) => {},
    tripFormSaving = false,
    tripFormError = null,
    currentParentId = null,
    activeTripRequest = null,
    onCloseTripRequest = () => {},
    onOpenParentMeetingInvitation = (_meeting: any) => {},
    activeParentMeetingInvitation = null,
    onCloseParentMeetingInvitation = () => {},
    onParentMeetingInvitationConfirmed = () => {},
  } = props;

  const [expandedRequestId, setExpandedRequestId] = useState<any>(null);

  const pendingRequests = Array.isArray(requests)
    ? requests.filter(
        (req: any) => String(req?.status || "").toUpperCase() === "PENDING",
      )
    : [];
  const isBankAccountRequest = (request: any) =>
    String(request?.entityType || "").toUpperCase() === "BANK_ACCOUNT";
  const onlyBankAccountPendingRequests =
    pendingRequests.length > 0 && pendingRequests.every(isBankAccountRequest);
  const openMeetingInvitations = Array.isArray(parentMeetingInvitations)
    ? parentMeetingInvitations.filter((invitation: any) =>
        ["INVITED", "RESERVED"].includes(
          String(invitation?.status || "").toUpperCase(),
        ),
      )
    : [];

  const directActionCount =
    missingChildren.length + alumniChildren.length + outingActions.length;
  const waitingActionCount = privacyActions.length + healthActions.length + openMeetingInvitations.length;
  const requestCount = pendingRequests.length + waitingActionCount;
  const totalCount = directActionCount + requestCount;
  const hasActions = totalCount > 0;

  const formatDate = (value: any) => {
    if (value === null || value === undefined) return null;
    const num = Number(value);
    const millis = Number.isFinite(num)
      ? num > 1e12
        ? num
        : num * 1000
      : null;
    const date = millis ? new Date(millis) : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy}, ${hh}:${min}`;
  };

  const formatRange = (start: any, end: any) => {
    const a = formatDate(start)?.split(",")[0];
    const b = formatDate(end)?.split(",")[0];
    if (a && b) return `${a} - ${b}`;
    return a || b || null;
  };

  const meetingToken = (meeting: any) =>
    meeting?.token ||
    meeting?.confirmationToken ||
    meeting?.invitationToken ||
    null;

  const isSelectedMeetingActive = () => {
    if (!selectedMeeting || !activeParentMeetingInvitation) return false;
    const selectedToken = meetingToken(selectedMeeting);
    const activeToken = meetingToken(activeParentMeetingInvitation);
    return (
      (selectedMeeting.id &&
        activeParentMeetingInvitation.id &&
        String(selectedMeeting.id) ===
          String(activeParentMeetingInvitation.id)) ||
      (selectedToken &&
        activeToken &&
        String(selectedToken) === String(activeToken))
    );
  };

  const parsePayload = (payload: any) => {
    if (!payload || typeof payload !== "string") return null;
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  };

  const requestTitle = (req: any) => {
    if (isBankAccountRequest(req)) {
      return t("parentPortal.actions.bank_change_title", {
        defaultValue: "Bank account change",
      });
    }

    const parsed = parsePayload(req?.payload) || {};
    const payload = parsed?.payload || parsed?.child || parsed;
    const name = [payload?.firstName, payload?.lastName]
      .filter(Boolean)
      .join(" ");
    return (
      name ||
      req?.childName ||
      t("parentPortal.actions.request_unknown_title", {
        defaultValue: "Request",
      })
    );
  };

  const requestActionLabel = (action: any, req?: any) => {
    if (isBankAccountRequest(req)) {
      return t("parentPortal.actions.bank_change_action", {
        defaultValue: "Administrative update",
      });
    }

    const normalized = String(action || "").toUpperCase();
    if (normalized === "CREATE")
      return t("parentPortal.actions.request_create", {
        defaultValue: "New child registration",
      });
    if (normalized === "UPDATE")
      return t("parentPortal.actions.request_update", {
        defaultValue: "Change request",
      });
    if (normalized === "DELETE")
      return t("parentPortal.actions.request_delete", {
        defaultValue: "Delete request",
      });
    return t("parentPortal.actions.request_generic", {
      defaultValue: "Pending request",
    });
  };

  const requestStatusLabel = (status: any, req?: any) => {
    if (isBankAccountRequest(req)) {
      return t("parentPortal.bank_change_status", {
        defaultValue: "Change received",
      });
    }

    const normalized = String(status || "").toUpperCase();
    if (normalized === "PENDING")
      return t("parentPortal.actions.status_pending_plain", {
        defaultValue: "Pending",
      });
    if (normalized === "APPROVED")
      return t("parentPortal.actions.status_approved", {
        defaultValue: "Approved",
      });
    if (normalized === "REJECTED")
      return t("parentPortal.actions.status_rejected", {
        defaultValue: "Rejected",
      });
    if (normalized === "COMPLETED")
      return t("parentPortal.actions.status_completed", {
        defaultValue: "Completed",
      });
    return (
      status ||
      t("parentPortal.actions.status_unknown", { defaultValue: "Unknown" })
    );
  };

  const requestExplanation = (req: any) => {
    if (isBankAccountRequest(req)) {
      return t("parentPortal.bank_change_pending", {
        defaultValue:
          "Your new bank account number has been saved and is ready for administrative processing.",
      });
    }

    const action = String(req?.action || "").toUpperCase();
    const status = String(req?.status || "").toUpperCase();
    if (status === "PENDING" && action === "CREATE") {
      return t("parentPortal.actions.pending_create_explanation", {
        defaultValue:
          "This registration has been submitted and is waiting to be processed by the school. You do not need to do anything right now.",
      });
    }
    if (status === "PENDING" && action === "UPDATE") {
      return t("parentPortal.actions.pending_update_explanation", {
        defaultValue:
          "This change has been submitted and is waiting to be processed by the school. You do not need to do anything right now.",
      });
    }
    return t("parentPortal.actions.pending_generic_explanation", {
      defaultValue:
        "This request has been submitted and is waiting to be processed by the school. You do not need to do anything right now.",
    });
  };

  const isHiddenRequestField = (key: string) => {
    const normalized = String(key || "").toLowerCase();
    return (
      normalized === "id" ||
      normalized.endsWith("id") ||
      normalized === "uuid" ||
      normalized.endsWith("uuid") ||
      normalized.includes("bsn") ||
      normalized.includes("socialsecurity") ||
      normalized.includes("social_security") ||
      normalized.includes("burgerservice")
    );
  };

  const readableFieldLabel = (key: string) => {
    const spaced = String(key || "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .trim();
    if (!spaced) return "—";
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const requestDetails = (req: any) => {
    const parsed = parsePayload(req?.payload) || {};
    const payload = parsed?.payload || parsed?.child || parsed;
    if (!payload || typeof payload !== "object" || Array.isArray(payload))
      return [];

    const preferredKeys = [
      "firstName",
      "lastName",
      "gender",
      "dateOfBirth",
      "dateOfRegistration",
      "email",
      "phone",
    ];
    const keys = [
      ...preferredKeys.filter((key) =>
        Object.prototype.hasOwnProperty.call(payload, key),
      ),
      ...Object.keys(payload).filter((key) => !preferredKeys.includes(key)),
    ];

    return keys
      .filter((key) => !isHiddenRequestField(key))
      .map((key) => {
        const value = payload[key];
        if (value === null || value === undefined || value === "") return null;
        if (typeof value === "object") return null;
        return {
          key,
          label: readableFieldLabel(key),
          value: String(value),
        };
      })
      .filter(Boolean)
      .slice(0, 8);
  };

  const entityLabel = (entityType: any) => {
    const normalized = String(entityType || "").toUpperCase();
    if (normalized === "CHILD")
      return t("parentPortal.actions.entity_child", { defaultValue: "Child" });
    if (normalized === "FAMILY")
      return t("parentPortal.actions.entity_family", {
        defaultValue: "Family",
      });
    if (normalized === "PARENT")
      return t("parentPortal.actions.entity_parent", {
        defaultValue: "Parent",
      });
    return t("parentPortal.actions.entity_request", {
      defaultValue: "Request",
    });
  };

  const isActiveTripRequest = (req: any) => {
    if (!activeTripRequest?.id || !req?.id) return false;
    return String(activeTripRequest.id) === String(req.id);
  };

  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);

  const urgentCount =
    missingChildren.length + alumniChildren.length + outingActions.length;
  const waitingCount = waitingActionCount;
  const inProgressCount = pendingRequests.length;

  // The trip consent form opens in the detail panel on the right.
  const tripFormOpen =
    !!activeTripRequest &&
    !activeTripRequest.approveOnly &&
    outingActions.some((req: any) => isActiveTripRequest(req));

  const handleSelectRequest = (req: any) => {
    if (tripFormOpen) onCloseTripRequest();
    setSelectedMeeting(null);
    setSelectedRequest(req);
    setExpandedRequestId(req?.id || null);
  };

  const handleSelectMeeting = (invitation: any) => {
    if (tripFormOpen) onCloseTripRequest();
    setSelectedRequest(null);
    setExpandedRequestId(null);
    setSelectedMeeting(invitation);
  };

  const detailRequest = selectedMeeting || tripFormOpen
    ? null
    : selectedRequest || pendingRequests[0] || null;
  const detailItems = detailRequest ? requestDetails(detailRequest) : [];
  const selectedMeetingFlowOpen =
    !!selectedMeeting && isSelectedMeetingActive() && !!meetingToken(selectedMeeting);
  const activeDetailKey = tripFormOpen
    ? `trip-${activeTripRequest.id}`
    : selectedMeeting
    ? `meeting-${meetingToken(selectedMeeting) || selectedMeeting.id || selectedMeeting.childName || "active"}-${selectedMeetingFlowOpen ? "flow" : "details"}`
    : detailRequest
      ? `request-${detailRequest.id || requestTitle(detailRequest)}`
      : "empty";

  const Stat = ({ tone, icon, value, label }: any) => {
    const toneClass =
      tone === "rose"
        ? "bg-rose-50 text-rose-700 ring-rose-100 dark:bg-rose-950/30 dark:text-rose-100 dark:ring-rose-900/60"
        : tone === "amber"
          ? "bg-amber-50 text-amber-700 ring-amber-100 dark:bg-amber-950/30 dark:text-amber-100 dark:ring-amber-900/60"
          : tone === "sky"
            ? "bg-sky-50 text-sky-700 ring-sky-100 dark:bg-sky-950/30 dark:text-sky-100 dark:ring-sky-900/60"
            : "bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-100 dark:ring-emerald-900/60";

    return (
      <div className="flex min-w-[9rem] items-center gap-3 border-l border-slate-100 px-5 first:border-l-0 dark:border-slate-800">
        <span
          className={cx(
            "inline-flex h-8 w-8 items-center justify-center rounded-full ring-1",
            toneClass,
          )}
        >
          {icon}
        </span>
        <div className="leading-none">
          <p className="text-lg font-bold leading-none text-slate-950 dark:text-white">
            {value}
          </p>
          <p className="mt-1.5 whitespace-nowrap text-xs font-semibold text-slate-500 dark:text-slate-400">
            {label}
          </p>
        </div>
      </div>
    );
  };

  const PendingRequestRow = ({ req }: any) => {
    const active =
      detailRequest?.id &&
      req?.id &&
      String(detailRequest.id) === String(req.id);
    return (
      <button
        type="button"
        onClick={() => handleSelectRequest(req)}
        className={cx(
          "group flex w-full items-center gap-4 border-b border-slate-100 px-3.5 py-2.5 text-left transition last:border-b-0 dark:border-slate-800/80",
          active
            ? "bg-sky-50/70 dark:bg-sky-950/20"
            : "hover:bg-slate-50/80 dark:hover:bg-slate-900/50",
        )}
      >
        <IconBubble tone="sky" compact>
          <FileTextIcon size={18} />
        </IconBubble>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-[15px] font-bold text-slate-950 dark:text-white">
              {requestTitle(req)}
            </p>
            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 ring-1 ring-amber-100 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-800/60">
              {requestStatusLabel(req.status)}
            </span>
          </div>
          <p className="mt-0.5 truncate text-sm text-slate-500 dark:text-slate-300">
            {entityLabel(req.entityType)} · {requestActionLabel(req.action)}
          </p>
          {req.createdAt && (
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              {t("parentPortal.actions.requested_at", {
                defaultValue: "Requested at {{date}}",
                date: formatDate(req.createdAt),
              })}
            </p>
          )}
        </div>
        <ChevronRightIcon
          size={21}
          className={cx(
            "shrink-0 text-slate-400 transition group-hover:translate-x-0.5",
            active && "text-sky-600",
          )}
        />
      </button>
    );
  };

  return (
    <div className="parent-portal-actions-tab mx-auto w-full max-w-5xl space-y-5 text-left">
      <header className="flex flex-col gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-5 ring-1 ring-emerald-100 dark:from-emerald-950/20 dark:to-slate-950/20 dark:ring-emerald-900/50 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm" aria-hidden="true">
            <CheckCircleIcon />
          </span>
          <span className="min-w-0">
            <h2 className="text-2xl font-black tracking-tight text-slate-950 dark:text-slate-50">
              {t("parentPortal.actions.open_requests_title", { defaultValue: "Open requests" })}
            </h2>
            <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
              {t("parentPortal.actions.open_requests_description", {
                defaultValue: "Track submitted requests, invitations and items that still need information.",
              })}
            </p>
          </span>
        </div>

        {hasActions && (
          <div className="grid w-full grid-cols-3 items-center overflow-hidden rounded-2xl border border-emerald-100 bg-white/75 py-2 shadow-sm dark:border-emerald-900/50 dark:bg-slate-900/40 lg:w-auto lg:shrink-0">
            <Stat
              tone="rose"
              icon={<AlertTriangleIcon size={18} />}
              value={urgentCount}
              label={t("parentPortal.actions.urgent", {
                defaultValue: "Urgent",
              })}
            />
            <Stat
              tone="amber"
              icon={<ClockIcon size={18} />}
              value={waitingCount}
              label={t("parentPortal.actions.waiting_for_you", {
                defaultValue: "Waiting for you",
              })}
            />
            <Stat
              tone="sky"
              icon={<HourglassIcon size={18} />}
              value={inProgressCount}
              label={t("parentPortal.actions.in_progress", {
                defaultValue: "In progress",
              })}
            />
          </div>
        )}
      </header>

      {!hasActions ? (
        <AppCard className="flex items-center gap-3 px-5 py-5 text-sm text-slate-500 dark:text-slate-300">
          <IconBubble tone="emerald" compact>
            <CheckCircleIcon size={20} />
          </IconBubble>
          {t("parentPortal.actions.no_requests_body", {
            defaultValue: "All requests are complete.",
          })}
        </AppCard>
      ) : (
        <div
          className="grid gap-4 xl:items-start"
          style={{
            // With the trip consent form open the form gets the room; the list on the left narrows.
            gridTemplateColumns: tripFormOpen
              ? "minmax(20rem, 0.7fr) minmax(0, 1.3fr)"
              : "minmax(24rem, 0.95fr) minmax(0, 1.05fr)",
            transition:
              "grid-template-columns 260ms cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "grid-template-columns",
          }}
        >
          <SlideIn direction="left" className="min-w-0 space-y-4">
            {waitingActionCount > 0 && (
              <section>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300">
                  {t("parentPortal.actions.waiting_for_you", {
                    defaultValue: "Waiting for you",
                  })}
                </h3>
                <div className="space-y-3">
                  {privacyActions.map((action: any) => (
                    <button
                      key={action.id || `privacy-${action.child?.id || action.childName}`}
                      type="button"
                      onClick={() => onOpenPrivacyAction(action.child)}
                      className="group flex w-full items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 px-3.5 py-2.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-50 hover:shadow-md dark:border-amber-800/60 dark:bg-amber-950/30 dark:hover:bg-amber-950/40"
                    >
                      <IconBubble tone="amber" compact>
                        <FileTextIcon size={19} />
                      </IconBubble>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-200">
                          {t("parentPortal.actions.privacy_badge", {
                            defaultValue: "Privacy",
                          })}
                        </p>
                        <p className="mt-1 text-[15px] font-bold text-slate-950 dark:text-white">
                          {t("parentPortal.actions.privacy_title", {
                            defaultValue: "Set privacy preferences",
                          })}
                        </p>
                        <p className="mt-0.5 truncate text-sm text-slate-600 dark:text-slate-300">
                          {t("parentPortal.actions.privacy_description", {
                            defaultValue:
                              "Choose how photos and information about {{childName}} may be used.",
                            childName: action.childName,
                          })}
                        </p>
                      </div>
                      <span className="hidden rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800 ring-1 ring-amber-200 transition group-hover:bg-amber-200 md:inline-flex dark:bg-amber-900/50 dark:text-amber-100 dark:ring-amber-800/60">
                        {t("parentPortal.actions.open", {
                          defaultValue: "Open",
                        })}
                      </span>
                      <ChevronRightIcon
                        size={22}
                        className="shrink-0 text-amber-700 transition group-hover:translate-x-0.5"
                      />
                    </button>
                  ))}

                  {healthActions.map((action: any) => (
                    <button
                      key={action.id || `health-${action.child?.id || action.childName}`}
                      type="button"
                      onClick={() => onOpenHealthAction(action.child)}
                      className="group flex w-full items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 px-3.5 py-2.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-50 hover:shadow-md dark:border-amber-800/60 dark:bg-amber-950/30 dark:hover:bg-amber-950/40"
                    >
                      <IconBubble tone="amber" compact>
                        <FileTextIcon size={19} />
                      </IconBubble>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-200">
                          {t("parentPortal.health.action_badge", { defaultValue: "Contacts & notes" })}
                        </p>
                        <p className="mt-1 text-[15px] font-bold text-slate-950 dark:text-white">
                          {t("parentPortal.health.action_title", { defaultValue: "Check contacts & notes" })}
                        </p>
                        <p className="mt-0.5 truncate text-sm text-slate-600 dark:text-slate-300">
                          {t("parentPortal.health.action_description", {
                            defaultValue: "New school year: are the doctor, emergency contact and allergies of {{childName}} still correct?",
                            childName: action.childName,
                          })}
                        </p>
                      </div>
                      <span className="hidden rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800 ring-1 ring-amber-200 transition group-hover:bg-amber-200 md:inline-flex dark:bg-amber-900/50 dark:text-amber-100 dark:ring-amber-800/60">
                        {t("parentPortal.actions.open", { defaultValue: "Open" })}
                      </span>
                      <ChevronRightIcon size={22} className="shrink-0 text-amber-700 transition group-hover:translate-x-0.5" />
                    </button>
                  ))}

                  {openMeetingInvitations.map((invitation: any) => (
                    <button
                      key={`meeting-${invitation.id}`}
                      type="button"
                      onClick={() => handleSelectMeeting(invitation)}
                      className={cx(
                        "group flex w-full items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/70 px-3.5 py-2.5 text-left shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-50 hover:shadow-md dark:border-amber-800/60 dark:bg-amber-950/30 dark:hover:bg-amber-950/40",
                        selectedMeeting?.id &&
                          invitation?.id &&
                          String(selectedMeeting.id) ===
                            String(invitation.id) &&
                          "ring-2 ring-amber-200 dark:ring-amber-700/50",
                      )}
                    >
                      <IconBubble tone="amber" compact>
                        <ClockIcon size={19} />
                      </IconBubble>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-200">
                          {t("parentPortal.actions.invitation_ready", {
                            defaultValue: "Invitation is ready",
                          })}
                        </p>
                        <p className="mt-1 text-[15px] font-bold text-slate-950 dark:text-white">
                          {t("parentPortal.parent_meetings.title", {
                            defaultValue: "Parent meeting",
                          })}
                        </p>
                        <p className="mt-0.5 truncate text-sm text-slate-600 dark:text-slate-300">
                          {[
                            invitation.childName,
                            invitation.classroomName,
                            invitation.teacherName,
                          ]
                            .filter(Boolean)
                            .join(" · ") ||
                            t(
                              "parentPortal.parent_meetings.invitation_subtitle",
                              {
                                defaultValue: "Meeting invitation",
                              },
                            )}
                        </p>
                      </div>
                      <span className="hidden rounded-full bg-amber-100 px-3 py-1.5 text-xs font-bold text-amber-800 ring-1 ring-amber-200 transition group-hover:bg-amber-200 md:inline-flex dark:bg-amber-900/50 dark:text-amber-100 dark:ring-amber-800/60">
                        {t("parentPortal.actions.view_details", {
                          defaultValue: "View details",
                        })}
                      </span>
                      <ChevronRightIcon
                        size={22}
                        className="shrink-0 text-amber-700 transition group-hover:translate-x-0.5"
                      />
                    </button>
                  ))}
                </div>
              </section>
            )}

            {directActionCount > 0 && (
              <section>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-rose-700 dark:text-rose-300">
                  {t("parentPortal.actions.needs_response", {
                    defaultValue: "Needs response",
                  })}
                </h3>
                <AppCard className="overflow-hidden">
                  <ul>
                    {outingActions.map((req: any, index: number) => {
                      const outingTitle =
                        req.outingName ||
                        t("parentPortal.trip_consent.card_title_short", {
                          defaultValue: "Trip",
                        });
                      const outingRange = formatRange(
                        req.startsAt,
                        req.expiresAt,
                      );
                      const dual = !!req.requiresDualConsent;
                      const isParent1 =
                        currentParentId &&
                        req.parent1Id &&
                        String(req.parent1Id) === String(currentParentId);
                      const isParent2 =
                        currentParentId &&
                        req.parent2Id &&
                        String(req.parent2Id) === String(currentParentId);
                      const parent1Done = !!req.parent1ConsentedAt;
                      const parent2Done = !!req.parent2ConsentedAt;
                      const currentDone =
                        (isParent1 && parent1Done) ||
                        (isParent2 && parent2Done);
                      const otherDone =
                        (isParent1 && parent2Done) ||
                        (isParent2 && parent1Done) ||
                        (!isParent1 &&
                          !isParent2 &&
                          (parent1Done || parent2Done));

                      const description = (() => {
                        if (dual && otherDone && !currentDone) {
                          return t(
                            "parentPortal.trip_consent.approval_needed",
                            {
                              defaultValue:
                                "Information has been filled in. Please approve or reject.",
                            },
                          );
                        }
                        if (dual && currentDone && !otherDone) {
                          return t(
                            "parentPortal.trip_consent.dual_consent_waiting",
                            {
                              defaultValue:
                                "Waiting for approval from the other parent.",
                            },
                          );
                        }
                        return t("parentPortal.trip_consent.card_title", {
                          defaultValue:
                            "Fill in the consent form for this trip.",
                        });
                      })();

                      return (
                        <React.Fragment
                          key={`outing-${req.id || req.childId || req.targetUrl || req.label || index}`}
                        >
                          <RequestRow
                            icon={<FileTextIcon size={19} />}
                            tone="amber"
                            // The whole row opens the form, like the other rows; approve/reject keeps its own buttons.
                            onClick={dual && otherDone !== currentDone ? undefined : () => onOpenOuting(req)}
                            title={
                              req.childName ||
                              t("common.unknown_child", {
                                defaultValue: "Child",
                              })
                            }
                            subtitle={[outingTitle, description]
                              .filter(Boolean)
                              .join(" — ")}
                            meta={
                              outingRange
                                ? t("parentPortal.actions.date_range", {
                                    defaultValue: "Date: {{range}}",
                                    range: outingRange,
                                  })
                                : null
                            }
                            badge={t("parentPortal.actions.trip_badge", {
                              defaultValue: "Trip",
                            })}
                          >
                            {dual && otherDone && !currentDone ? (
                              <>
                                <PrimaryButton
                                  onClick={() =>
                                    onOpenOuting({
                                      ...req,
                                      approveOnly: true,
                                      approve: true,
                                    })
                                  }
                                >
                                  {t("parentPortal.trip_consent.approve", {
                                    defaultValue: "Approve",
                                  })}
                                </PrimaryButton>
                                <SecondaryButton
                                  tone="rose"
                                  onClick={() =>
                                    onOpenOuting({
                                      ...req,
                                      approveOnly: true,
                                      approve: false,
                                    })
                                  }
                                >
                                  {t("parentPortal.trip_consent.reject", {
                                    defaultValue: "Reject",
                                  })}
                                </SecondaryButton>
                              </>
                            ) : dual && currentDone && !otherDone ? (
                              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800 shadow-sm dark:border-amber-700/60 dark:bg-amber-900/40 dark:text-amber-100">
                                {t(
                                  "parentPortal.trip_consent.dual_consent_waiting_short",
                                  { defaultValue: "Waiting" },
                                )}
                              </span>
                            ) : (
                              <PrimaryButton onClick={() => onOpenOuting(req)}>
                                {t("parentPortal.trip_consent.open", {
                                  defaultValue: "Open",
                                })}
                                <ChevronRightIcon size={15} />
                              </PrimaryButton>
                            )}
                          </RequestRow>

                        </React.Fragment>
                      );
                    })}

                    {missingChildren.map((child: any) => (
                      <RequestRow
                        key={`missing-${child.id}`}
                        icon={<AlertTriangleIcon size={19} />}
                        tone="amber"
                        onClick={() => onFixChild(child)}
                        title={
                          [child.firstName, child.lastName]
                            .filter(Boolean)
                            .join(" ") ||
                          t("common.unknown_child", { defaultValue: "Child" })
                        }
                        subtitle={t("parentPortal.actions.missing_bsn", {
                          defaultValue:
                            "BSN missing — please provide this social security number.",
                        })}
                        badge={t("parentPortal.actions.missing_bsn_badge", {
                          defaultValue: "Missing",
                        })}
                      >
                        <PrimaryButton onClick={() => onFixChild(child)}>
                          {t("parentPortal.actions.fix_bsn", {
                            defaultValue: "Add BSN",
                          })}
                        </PrimaryButton>
                      </RequestRow>
                    ))}

                    {alumniChildren.map((child: any) => (
                      <RequestRow
                        key={`alumni-${child.id}`}
                        icon={<FileTextIcon size={19} />}
                        tone="amber"
                        onClick={() => onAlumniAction?.(child.id)}
                        title={
                          [child.firstName, child.lastName]
                            .filter(Boolean)
                            .join(" ") ||
                          t("common.unknown_child", { defaultValue: "Child" })
                        }
                        subtitle={t("parentPortal.actions.alumni_prompt", {
                          defaultValue:
                            "Fill in alumni preferences for this child.",
                        })}
                        badge={t("parentPortal.actions.alumni_eyebrow", {
                          defaultValue: "Alumni",
                        })}
                      >
                        <PrimaryButton
                          className="bg-amber-500 hover:bg-amber-400"
                          onClick={() => onAlumniAction?.(child.id)}
                        >
                          {t("parentPortal.actions.alumni_cta", {
                            defaultValue: "Alumni preferences",
                          })}
                          <ChevronRightIcon size={15} />
                        </PrimaryButton>
                      </RequestRow>
                    ))}
                  </ul>
                </AppCard>
              </section>
            )}

            {pendingRequests.length > 0 && (
              <section>
                <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300">
                  {t("parentPortal.actions.in_progress", {
                    defaultValue: onlyBankAccountPendingRequests
                      ? "Received changes"
                      : "In progress",
                  })}
                </h3>
                <AppCard className="overflow-hidden">
                  <ul>
                    {pendingRequests.map((req: any) => (
                      <PendingRequestRow key={`request-${req.id}`} req={req} />
                    ))}
                  </ul>
                </AppCard>
              </section>
            )}
          </SlideIn>

          <SlideIn direction="right" className="min-w-0">
            <AppCard className={cx("overflow-hidden p-5", !tripFormOpen && "sticky top-4")}>
              <div key={activeDetailKey} className="parent-portal-actions-detail-switch">
              {tripFormOpen ? (
                <TripConsentFormAny
                  t={t}
                  request={activeTripRequest}
                  form={tripFormDraft}
                  onChange={onTripFormChange}
                  onSubmit={onTripFormSubmit}
                  onCancel={onCloseTripRequest}
                  submitting={tripFormSaving}
                  error={tripFormError}
                />
              ) : selectedMeeting ? (
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="truncate text-lg font-bold text-slate-950 dark:text-white">
                        {selectedMeetingFlowOpen
                          ? t("parentPortal.actions.choose_meeting_time_title", {
                              defaultValue: "Choose a time slot",
                            })
                          : t("parentPortal.actions.meeting_invitation_details_title", {
                              defaultValue: "Invitation details",
                            })}
                      </h3>
                      <span className="mt-2 inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 ring-1 ring-amber-100 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-800/60">
                        {t("parentPortal.actions.invitation_ready", {
                          defaultValue: "Invitation is ready",
                        })}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedMeeting(null)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                      aria-label={t("common.close", { defaultValue: "Close" })}
                    >
                      ×
                    </button>
                  </div>

                  <div className="mt-5">
                    {!selectedMeetingFlowOpen && (
                      <dl className="grid gap-2 text-sm sm:grid-cols-3">
                        <div className="rounded-2xl bg-slate-50 px-3 py-2 dark:bg-slate-900/60">
                          <dt className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
                            {t("parentPortal.children.child", {
                              defaultValue: "Child",
                            })}
                          </dt>
                          <dd className="mt-1 truncate font-semibold text-slate-950 dark:text-white">
                            {selectedMeeting.childName || "—"}
                          </dd>
                        </div>
                        <div className="rounded-2xl bg-slate-50 px-3 py-2 dark:bg-slate-900/60">
                          <dt className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
                            {t("parentPortal.children.class", {
                              defaultValue: "Class",
                            })}
                          </dt>
                          <dd className="mt-1 truncate font-semibold text-slate-950 dark:text-white">
                            {selectedMeeting.classroomName || "—"}
                          </dd>
                        </div>
                        <div className="rounded-2xl bg-slate-50 px-3 py-2 dark:bg-slate-900/60">
                          <dt className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
                            {t("parentPortal.actions.teacher", {
                              defaultValue: "Teacher",
                            })}
                          </dt>
                          <dd className="mt-1 truncate font-semibold text-slate-950 dark:text-white">
                            {selectedMeeting.teacherName || "—"}
                          </dd>
                        </div>
                      </dl>
                    )}

                    <div className={cx("min-w-0", selectedMeetingFlowOpen ? "mt-2" : "mt-4")}>
                      {isSelectedMeetingActive() &&
                      meetingToken(selectedMeeting) ? (
                        <div className="parent-meeting-inline-safe overflow-visible rounded-3xl bg-white/70 px-2 py-1 shadow-none dark:bg-slate-900/40 [&_*]:overflow-visible [&_select]:min-w-full [&_button]:whitespace-nowrap [&_button]:pl-6 [&_button]:pr-6 [&_h1]:pl-1 [&_h2]:pl-1 [&_h3]:pl-1">
                          <ParentMeetingInvitationInlineCard
                            token={meetingToken(selectedMeeting)}
                            onCancel={onCloseParentMeetingInvitation}
                            onConfirmed={onParentMeetingInvitationConfirmed}
                          />
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-amber-100 bg-amber-50/70 p-4 dark:border-amber-900/60 dark:bg-amber-950/30">
                          <p className="text-sm leading-6 text-amber-900 dark:text-amber-100">
                            {t("parentPortal.actions.meeting_calendar_explanation", {
                              defaultValue:
                                "Choose a time slot for this parent meeting. The appointment will also appear in the calendar.",
                            })}
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              onOpenParentMeetingInvitation(selectedMeeting)
                            }
                            disabled={!meetingToken(selectedMeeting)}
                            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {t("parentPortal.actions.choose_time_slot", {
                              defaultValue: "Choose time slot",
                            })}
                            <ChevronRightIcon size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : detailRequest ? (
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="truncate text-lg font-bold text-slate-950 dark:text-white">
                        {requestTitle(detailRequest)}
                      </h3>
                      <span className="mt-2 inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 ring-1 ring-amber-100 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-800/60">
                        {requestStatusLabel(detailRequest.status, detailRequest)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedRequest(null);
                        setSelectedMeeting(null);
                      }}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                      aria-label={t("common.close", { defaultValue: "Close" })}
                    >
                      ×
                    </button>
                  </div>

                  <div className="mt-5 space-y-2.5">
                    <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 text-sm">
                      <span className="font-semibold text-slate-500 dark:text-slate-400">
                        {t("parentPortal.actions.detail_type", {
                          defaultValue: "Request type",
                        })}
                      </span>
                      <span className="font-semibold text-slate-950 dark:text-white">
                        {entityLabel(detailRequest.entityType)} ·{" "}
                        {requestActionLabel(detailRequest.action, detailRequest)}
                      </span>
                      <span className="font-semibold text-slate-500 dark:text-slate-400">
                        {t("parentPortal.actions.detail_status", {
                          defaultValue: "Status",
                        })}
                      </span>
                      <span className="font-semibold text-slate-950 dark:text-white">
                        {requestStatusLabel(detailRequest.status, detailRequest)}
                      </span>
                      <span className="font-semibold text-slate-500 dark:text-slate-400">
                        {t("parentPortal.actions.detail_submitted", {
                          defaultValue: "Submitted",
                        })}
                      </span>
                      <span className="font-semibold text-slate-950 dark:text-white">
                        {formatDate(detailRequest.createdAt) || "—"}
                      </span>
                    </div>
                  </div>

                  {detailItems.length > 0 && (
                    <div className="mt-5">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        {t("parentPortal.actions.detail_data", {
                          defaultValue: "Submitted details",
                        })}
                      </p>
                      <dl className="mt-3 space-y-2">
                        {detailItems.map((item: any) => (
                          <div
                            key={item.key}
                            className="flex items-center justify-between gap-4 rounded-xl bg-slate-50 px-3 py-1.5 text-sm dark:bg-slate-900/60"
                          >
                            <dt className="text-slate-500 dark:text-slate-400">
                              {item.label}
                            </dt>
                            <dd className="max-w-[12rem] truncate text-right font-semibold text-slate-950 dark:text-white">
                              {item.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}

                  <div className="mt-4 rounded-2xl border border-sky-100 bg-sky-50/70 p-4 text-sm leading-6 text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-100">
                    {requestExplanation(detailRequest)}
                  </div>
                </div>
              ) : (
                <div className="flex min-h-[16rem] flex-col items-center justify-center text-center text-sm text-slate-500 dark:text-slate-300">
                  <IconBubble tone="emerald">
                    <FileTextIcon size={22} />
                  </IconBubble>
                  <p className="mt-3 font-semibold text-slate-700 dark:text-slate-100">
                    {t("parentPortal.actions.select_request_title", {
                      defaultValue: "Select a request",
                    })}
                  </p>
                  <p className="mt-1 max-w-xs">
                    {t("parentPortal.actions.select_request_description", {
                      defaultValue:
                        "Click a request or invitation to view the type, status and details.",
                    })}
                  </p>
                </div>
              )}
              </div>
            </AppCard>
          </SlideIn>
        </div>
      )}

      <style>{`
        @keyframes lumenPortalScreenSlideIn {
          from { opacity: 0; transform: translateX(32px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes lumenPortalContentIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes parentPortalActionsDetailSwitch {
          from { opacity: 0; transform: translateX(18px) scale(0.985); filter: blur(2px); }
          to { opacity: 1; transform: translateX(0) scale(1); filter: blur(0); }
        }
        .parent-portal-actions-detail-switch {
          animation: parentPortalActionsDetailSwitch 280ms cubic-bezier(0.22, 1, 0.36, 1) both;
          will-change: opacity, transform, filter;
        }
        .parent-portal-actions-tab {
          animation: lumenPortalScreenSlideIn 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .parent-meeting-inline-safe h1,
        .parent-meeting-inline-safe h2,
        .parent-meeting-inline-safe h3,
        .parent-meeting-inline-safe button {
          overflow: visible;
          text-rendering: geometricPrecision;
        }
        .parent-meeting-inline-safe h1,
        .parent-meeting-inline-safe h2,
        .parent-meeting-inline-safe h3 {
          padding-left: 0.125rem;
        }
        @media (prefers-reduced-motion: reduce) {
          .parent-portal-actions-tab,
          .parent-portal-actions-detail-switch { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default ActionsTab;
